<script lang="ts">
	import type { Device } from 'src/types/Device';
	import { format } from 'src/services/i18n';
	import { showNewLayout } from 'src/stores/toDelete/new_layout';
	import { getContext, onDestroy } from 'svelte';
	import type { Writable } from 'svelte/store';
	import type { SidepanelStickyBottomProps } from 'src/components/Sidepanel/sidepanel-types';
	import { Button, ButtonV2, SliderV2, Text, Toggle, toast } from '@Shelly/shelly-design-system';
	import {
		getCameraConfig,
		playCameraSound,
		saveCameraConfig,
	} from '../../helpers/camera-settings-helpers';
	import { hasCameraConfigKey } from '../../helpers/camera-features';
	import { getCameraSoundOptions } from '../../helpers/camera-sounds';
	import type { CameraAudioState } from '../../types/camera-settings';
	import type { CameraSoundName } from '../../types/camera-sounds';

	export let device: Device;

	const stickyBottomAction =
		getContext<Writable<SidepanelStickyBottomProps | null>>('stickyBottomAction');

	const supportsSounds = hasCameraConfigKey(device, 'sounds');
	const soundOptions = getCameraSoundOptions();

	const AUTOSAVE_DELAY = 500;

	const readAudio = (): CameraAudioState => {
		const config = getCameraConfig(device);
		return {
			inputEnable: config?.audio?.input?.enable ?? true,
			volume: config?.audio?.output?.volume ?? 100,
			soundsEnable: config?.sounds?.enable ?? false,
		};
	};

	let { inputEnable, volume, soundsEnable } = readAudio();
	let cacheInputEnable = inputEnable;
	let cacheVolume = volume;
	let cacheSoundsEnable = soundsEnable;
	let loading = false;
	let playingSound: CameraSoundName | null = null;
	let saveTimeout: ReturnType<typeof setTimeout> | undefined;

	$: hasChanges =
		inputEnable !== cacheInputEnable || volume !== cacheVolume || soundsEnable !== cacheSoundsEnable;

	$: if (stickyBottomAction) {
		if ($showNewLayout) {
			stickyBottomAction.set({
				onClick: resetToDefaults,
				loading,
				disabled: loading,
				label: $format('button_reset_to_defaults', { default: 'Reset to defaults' }),
			});
		} else {
			stickyBottomAction.set(null);
		}
	}

	const save = async (successMsg?: string) => {
		if (saveTimeout) {
			clearTimeout(saveTimeout);
			saveTimeout = undefined;
		}
		if (!hasChanges) return;
		loading = true;
		const success = await saveCameraConfig(
			device,
			{
				audio: {
					input: { enable: inputEnable },
					output: { volume },
				},
				...(supportsSounds && { sounds: { enable: soundsEnable } }),
			},
			successMsg ?? $format('setting_scc_saved_toast', { default: 'Setting saved successfully.' }),
		);
		if (success) {
			cacheInputEnable = inputEnable;
			cacheVolume = volume;
			cacheSoundsEnable = soundsEnable;
		} else {
			inputEnable = cacheInputEnable;
			volume = cacheVolume;
			soundsEnable = cacheSoundsEnable;
		}
		loading = false;
	};

	const scheduleSave = () => {
		if (saveTimeout) clearTimeout(saveTimeout);
		saveTimeout = setTimeout(() => save(), AUTOSAVE_DELAY);
	};

	const handleMicToggle = () => {
		inputEnable = !inputEnable;
		save(
			inputEnable
				? $format('camera_mic_enabled_toast', { default: 'Microphone enabled.' })
				: $format('camera_mic_disabled_toast', { default: 'Microphone disabled.' }),
		);
	};

	const handleSoundsToggle = () => {
		soundsEnable = !soundsEnable;
		save(
			soundsEnable
				? $format('camera_sounds_enabled_toast', { default: 'Camera sounds enabled.' })
				: $format('camera_sounds_disabled_toast', { default: 'Camera sounds disabled.' }),
		);
	};

	const playSound = async (sound: CameraSoundName) => {
		// A volume just moved on the slider only reaches the speaker once it is stored.
		await save();
		playingSound = sound;
		const played = await playCameraSound(device, sound);
		playingSound = null;

		if (!played) {
			toast.open({
				type: 'danger',
				title: $format('camera_play_sound_error', {
					default: 'Could not play the sound. Please retry.',
				}),
			});
		}
	};

	const resetToDefaults = async () => {
		loading = true;
		const success = await saveCameraConfig(
			device,
			{ audio: null, ...(supportsSounds && { sounds: null }) },
			$format('camera_audio_reset_success', { default: 'Audio settings reset to defaults.' }),
		);
		if (success) {
			({ inputEnable, volume, soundsEnable } = readAudio());
			cacheInputEnable = inputEnable;
			cacheVolume = volume;
			cacheSoundsEnable = soundsEnable;
		}
		loading = false;
	};

	onDestroy(() => {
		if (saveTimeout) {
			clearTimeout(saveTimeout);
			save();
		}
		stickyBottomAction?.set(null);
	});
</script>

<Text styleType="h2" class="mb-s">
	{$format('camera_audio_input', { default: 'Input' })}
</Text>

<Toggle
	size="m"
	class="mb-m"
	checked={inputEnable}
	disabled={loading}
	on:click={handleMicToggle}
	info={$format('camera_audio_input_info', {
		default: 'Enable or disable the microphone input.',
	})}
	id="camera-audio-input-enable"
>
	{$format('camera_audio_mic_enable', { default: 'Enable microphone' })}
</Toggle>

<Text styleType="h2" class="mb-s">
	{$format('camera_audio_output', { default: 'Output' })}
</Text>

<SliderV2
	id="camera-audio-volume"
	class="mb-s"
	value={volume}
	on:value_change={(e) => {
		volume = e.detail.value;
		scheduleSave();
	}}
	disabled={loading}
	min={0}
	max={100}
	step={1}
	unit="%"
	type="incremented"
	title="{$format('camera_audio_speaker_volume', { default: 'Speaker volume' })}:"
	titleTypography="body-small"
	a11yProps={{
		ariaLabel: $format('camera_audio_speaker_volume', { default: 'Speaker volume' }),
		ariaLabelValue: $format('camera_audio_speaker_volume_value', {
			default: 'Speaker volume value',
		}),
	}}
	lineMarkers={[
		{ value: 0, showLabel: true },
		{ value: 100, showLabel: true },
	]}
/>

{#if supportsSounds}
	<Text styleType="h2" class="mt-m mb-s">
		{$format('camera_audio_sounds', { default: 'Sounds' })}
	</Text>

	<Toggle
		size="m"
		class="mb-m"
		checked={soundsEnable}
		disabled={loading}
		on:click={handleSoundsToggle}
		info={$format('camera_sounds_info', {
			default:
				'Play the camera’s built-in sounds, such as the privacy mode and start-up notifications.',
		})}
		id="camera-sounds-enable"
	>
		{$format('camera_sounds_enable', { default: 'Enable camera sounds' })}
	</Toggle>

	<Text styleType="h2" class="mt-l mb-s">
		{$format('camera_play_sound', { default: 'Play a sound' })}
	</Text>

	<Text styleType="tiny" class="text-white mb-s">
		{$format('camera_play_sound_info', {
			default: 'Play a sound on the camera speaker to hear the current volume level.',
		})}
	</Text>

	<div class="d-flex flex-wrap gap-s mb-m">
		{#each soundOptions as sound (sound.id)}
			<ButtonV2
				variant="secondary"
				size="m"
				icon={sound.icon}
				loading={playingSound === sound.id}
				disabled={loading || playingSound !== null}
				on:click={() => playSound(sound.id)}
			>
				{sound.title}
			</ButtonV2>
		{/each}
	</div>
{/if}

{#if !$showNewLayout}
	<Button
		class="mt-m"
		variant="secondary"
		width="full"
		size="m"
		{loading}
		disabled={loading}
		on:click={resetToDefaults}
	>
		{$format('button_reset_to_defaults', { default: 'Reset to defaults' })}
	</Button>
{/if}
